import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, ArrowUpRight, Loader2, AlertCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Activate a service from the user's prepaid balance.
 *
 * - If the user is not logged in → redirect to login with next=...
 * - If balance is insufficient → show inline error + "Top up" link
 * - If activation succeeds → redirect to the subscription detail page
 */
export default function ActivateButton({
  serviceSlug,
  planCode,
  dailyCost = 0,
  monthlyCost = 0,
  children = "ACTIVATE",
  className = "btn-signal h-11 px-6 text-sm font-mono tracking-widest",
  testId = "activate-button",
  fullWidth = false,
}) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [needsTopup, setNeedsTopup] = useState(false);

  const onClick = async (e) => {
    e.preventDefault();
    if (busy) return;
    setErr("");
    setNeedsTopup(false);

    if (!user || !user.id) {
      const next = encodeURIComponent(`/services/${serviceSlug}`);
      nav(`/login?next=${next}`);
      return;
    }

    setBusy(true);
    try {
      const { data } = await api.post("/subscriptions/activate", {
        service_slug: serviceSlug,
        plan_code: planCode,
      });
      const subId = data.subscription_id;
      if (subId) {
        nav(`/subscriptions/${subId}`);
        return;
      }
      throw new Error("Activation did not return a subscription id");
    } catch (e) {
      const msg = formatApiError(e);
      setErr(msg);
      // 402 — backend says insufficient balance. Show a top-up link.
      if (e?.response?.status === 402) setNeedsTopup(true);
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        data-testid={testId}
        className={`${className} ${fullWidth ? "w-full" : ""} disabled:opacity-60 disabled:cursor-not-allowed`}
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
        <span>
          {busy ? "ACTIVATING…" : children}
        </span>
        {!busy && <ArrowUpRight size={12} className="opacity-70" />}
      </button>
      {err && (
        <div
          data-testid="activate-error"
          className="mt-2 border border-red-900/60 bg-red-950/30 text-red-300 text-[12px] p-2.5 rounded-sm font-mono flex items-start gap-2"
        >
          <AlertCircle size={13} className="mt-0.5 shrink-0" />
          <div>
            <div>{err}</div>
            {needsTopup && (
              <a
                href="/topup"
                onClick={(ev) => {
                  ev.preventDefault();
                  nav("/topup");
                }}
                className="mt-1 inline-block text-signal hover:underline"
              >
                Top up now →
              </a>
            )}
          </div>
        </div>
      )}
    </>
  );
}
